/* ─────────────────────────────────────────────────────────────────────────────
   VENTO PRODUCT PAGE — vento-product.js
   Asset path: shopify/assets/vento-product.js

   Reads per-variant image galleries from window.vpVariantGalleries
   which is populated in main-product.liquid using these metafields:
     custom.c1_black
     custom.c2_green_fade
     custom.c4_grey
     custom.blue_fade
───────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── DOM REFS ─────────────────────────────────────────────────────────────────
  const mainImage     = document.getElementById('vpMainImage');
  const thumbGallery  = document.getElementById('vpThumbGallery');
  const variantSelect = document.getElementById('vpVariantSelect');
  const formVariantId = document.getElementById('vpFormVariantId');
  const priceEl       = document.getElementById('vpPrice');
  const product       = window.vpProductData;

  if (!mainImage || !product) return;

  // ── VARIANT GALLERIES from metafields (set in Liquid) ────────────────────────
  // Structure: { [variantId]: [ { src, thumb, large, alt }, ... ] }
  const variantGalleries = window.vpVariantGalleries || {};

  // ── FALLBACK: build gallery from native Shopify variant image assignment ──────
  // Used only if a variant has no metafield gallery assigned
  const nativeVariantImages = {};
  product.variants.forEach(function (v) {
    nativeVariantImages[v.id] = [];
  });
  product.images.forEach(function (img) {
    if (img.variant_ids && img.variant_ids.length > 0) {
      img.variant_ids.forEach(function (vid) {
        if (nativeVariantImages[vid]) {
          nativeVariantImages[vid].push({
            src:   img.src,
            large: getShopifyImageUrl(img.src, '900x'),
            thumb: getShopifyImageUrl(img.src, '200x'),
            alt:   img.alt || product.title,
          });
        }
      });
    }
  });

  // ── STATE ────────────────────────────────────────────────────────────────────
  const currentVariant = product.selected_or_first_available_variant || product.variants[0];
  let selectedOptions  = currentVariant ? [...currentVariant.options] : [];

  // ── HELPERS ──────────────────────────────────────────────────────────────────

  function getShopifyImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_' + size + '.$1');
  }

  function formatMoney(cents) {
    // Matches Shopify's default money format: $0.00 USD
    // Adjust symbol/format to match your shop's currency settings
    const amount = (cents / 100).toFixed(2);
    return '$' + amount + ' USD';
  }

  function getGalleryForVariant(variantId) {
    // 1. Try metafield gallery first
    const meta = variantGalleries[variantId];
    if (meta && meta.length > 0) return meta;

    // 2. Fallback: native variant image assignment
    const native = nativeVariantImages[variantId];
    if (native && native.length > 0) return native;

    // 3. Last resort: all product images
    return product.images.map(function (img) {
      return {
        src:   img.src,
        large: getShopifyImageUrl(img.src, '900x'),
        thumb: getShopifyImageUrl(img.src, '200x'),
        alt:   img.alt || product.title,
      };
    });
  }

  // ── MAIN IMAGE SWAP (with fade transition) ───────────────────────────────────

  function fadeSwapMainImage(src, alt) {
    mainImage.classList.add('vp-fade');
    setTimeout(function () {
      mainImage.src = src;
      mainImage.alt = alt || '';
      // Remove fade once loaded (or immediately if cached)
      if (mainImage.complete) {
        mainImage.classList.remove('vp-fade');
      } else {
        mainImage.onload = function () {
          mainImage.classList.remove('vp-fade');
        };
      }
    }, 220);
  }

  // ── THUMBNAIL GALLERY ────────────────────────────────────────────────────────

  function buildThumbs(images) {
    thumbGallery.innerHTML = '';

    if (!images || images.length === 0) return;

    images.forEach(function (imgData, i) {
      const wrap = document.createElement('div');
      wrap.className   = 'vp-thumb' + (i === 0 ? ' active' : '');
      wrap.dataset.large = imgData.large || imgData.src;
      wrap.dataset.alt   = imgData.alt || '';

      const img    = document.createElement('img');
      img.src      = imgData.thumb || imgData.src;
      img.alt      = imgData.alt || '';
      img.width    = 72;
      img.height   = 72;
      img.loading  = 'lazy';

      wrap.appendChild(img);
      wrap.addEventListener('click', function () {
        selectThumb(wrap, imgData.large || imgData.src, imgData.alt);
      });

      thumbGallery.appendChild(wrap);
    });
  }

  function selectThumb(thumbEl, largeSrc, alt) {
    // Remove active from all thumbs
    thumbGallery.querySelectorAll('.vp-thumb').forEach(function (t) {
      t.classList.remove('active');
    });
    thumbEl.classList.add('active');
    fadeSwapMainImage(largeSrc, alt);
  }

  // ── BIND CLICK on server-rendered initial thumbnails ─────────────────────────

  function bindInitialThumbs() {
    thumbGallery.querySelectorAll('.vp-thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        selectThumb(thumb, thumb.dataset.large, thumb.dataset.alt);
      });
    });
  }

  // ── VARIANT MATCHING ─────────────────────────────────────────────────────────

  function findVariantByOptions(options) {
    return product.variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === options[i];
      });
    });
  }

  // ── APPLY VARIANT — updates everything ───────────────────────────────────────

  function applyVariant(variant) {
    if (!variant) return;

    // 1. Update hidden select + form hidden input (Shopify cart requires this)
    if (variantSelect)  variantSelect.value  = variant.id;
    if (formVariantId)  formVariantId.value  = variant.id;

    // 2. Update URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // 3. Update price display
    if (priceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML =
          '<span class="vp-price__compare">' + formatMoney(variant.compare_at_price) + '</span>' +
          '<span class="vp-price__sale">'    + formatMoney(variant.price)             + '</span>';
      } else {
        priceEl.textContent = formatMoney(variant.price);
      }
    }

    // 4. Update add-to-cart button state
    const btn = document.querySelector('.vp-btn-lenses');
    if (btn) {
      btn.disabled    = !variant.available;
      btn.textContent = variant.available ? 'Select Your Lenses' : 'Out of Stock';
    }

    // 5. Fetch gallery for this variant (metafield → native → all product images)
    const gallery = getGalleryForVariant(variant.id);

    // 6. Swap main image to first image of new gallery
    if (gallery.length > 0) {
      fadeSwapMainImage(gallery[0].large || gallery[0].src, gallery[0].alt);
    }

    // 7. Rebuild thumbnail strip
    buildThumbs(gallery);
  }

  // ── SWATCH CLICK HANDLERS ─────────────────────────────────────────────────────

  document.querySelectorAll('.vp-swatch').forEach(function (swatch) {
    swatch.addEventListener('click', function () {
      const optionIndex = parseInt(this.dataset.optionIndex, 10);
      const value       = this.dataset.value;

      // Update selected options state
      selectedOptions[optionIndex] = value;

      // Update active swatch highlight
      const siblings = document.querySelectorAll(
        '.vp-swatches[data-option-index="' + optionIndex + '"] .vp-swatch'
      );
      siblings.forEach(function (s) { s.classList.remove('active'); });
      this.classList.add('active');

      // Update displayed colour name
      const nameEl = document.getElementById('vpOptionName' + optionIndex);
      if (nameEl) nameEl.textContent = value;

      // Find matching variant and apply
      const matched = findVariantByOptions(selectedOptions);
      if (matched) applyVariant(matched);
    });
  });

  // ── PILL CLICK HANDLERS ───────────────────────────────────────────────────────

  document.querySelectorAll('.vp-pill').forEach(function (pill) {
    pill.addEventListener('click', function () {
      const optionIndex = parseInt(this.dataset.optionIndex, 10);
      const value       = this.dataset.value;

      selectedOptions[optionIndex] = value;

      const siblings = document.querySelectorAll(
        '.vp-option-pills[data-option-index="' + optionIndex + '"] .vp-pill'
      );
      siblings.forEach(function (p) { p.classList.remove('active'); });
      this.classList.add('active');

      const nameEl = document.getElementById('vpOptionName' + optionIndex);
      if (nameEl) nameEl.textContent = value;

      const matched = findVariantByOptions(selectedOptions);
      if (matched) applyVariant(matched);
    });
  });

  // ── NATIVE SELECT FALLBACK ────────────────────────────────────────────────────

  if (variantSelect) {
    variantSelect.addEventListener('change', function () {
      const variant = product.variants.find(function (v) {
        return v.id === parseInt(variantSelect.value, 10);
      });
      if (variant) applyVariant(variant);
    });
  }

  // ── INIT ──────────────────────────────────────────────────────────────────────
  // Bind click events on the server-rendered thumbnails (first variant gallery)
  bindInitialThumbs();

})();
